import {
	fetchUserActivity,
	filterActivity,
	type ActivityCategory,
	type ActivityEntry,
	type TreasuryFlowLike,
} from "@/lib/activity";
import { useAuth } from "@/lib/auth";
import { loadClientActivity } from "@/lib/client-activity";
import { resolveIndexCanisterId } from "@/lib/icrc-history";
import { Principal } from "@icp-sdk/core/principal";
import { useQuery } from "@tanstack/react-query";

const DEFAULT_PAGE_SIZE = 15;

export function useWalletActivity(opts?: {
	category?: ActivityCategory | "all";
	asset?: string | "all";
	fromDay?: string;
	toDay?: string;
	page?: number;
	pageSize?: number;
}) {
	const {
		agent,
		actor,
		isAuthenticated,
		principal,
		isInitializing,
		ensureProtocolActor,
	} = useAuth();
	const page = opts?.page ?? 0;
	const pageSize = opts?.pageSize ?? DEFAULT_PAGE_SIZE;
	const category = opts?.category ?? "all";
	const asset = opts?.asset ?? "all";
	const fromDay = opts?.fromDay ?? "";
	const toDay = opts?.toDay ?? "";

	const usdmIdx = !!resolveIndexCanisterId("USDM");
	const mmkIdx = !!resolveIndexCanisterId("MMK");

	const query = useQuery({
		queryKey: [
			"v2",
			"wallet",
			"activity",
			principal ?? "anon",
			page,
			pageSize,
			category,
			asset,
			fromDay,
			toDay,
			usdmIdx,
			mmkIdx,
		],
		queryFn: async (): Promise<{
			entries: ActivityEntry[];
			total: number;
			page: number;
			pageSize: number;
			hasNext: boolean;
			sources: string[];
			protocolError: string | null;
		}> => {
			if (!principal || !isAuthenticated) {
				return {
					entries: [],
					total: 0,
					page,
					pageSize,
					hasNext: false,
					sources: [],
					protocolError: null,
				};
			}
			const p = Principal.fromText(principal);
			const needsClientFilter =
				asset !== "all" || fromDay !== "" || toDay !== "";
			const fetchLimit = needsClientFilter
				? Math.min(pageSize * 8, 120)
				: Math.min(pageSize * 3, 60);
			const offset = needsClientFilter ? 0 : page * pageSize;

			let liveActor = actor;
			let liveAgent = agent;
			if (!liveActor || !liveAgent) {
				const pair = await ensureProtocolActor();
				liveActor = pair?.actor ?? null;
				liveAgent = pair?.agent ?? null;
			}

			let protocolFlows: TreasuryFlowLike[] = [];
			let protocolError: string | null = null;
			try {
				if (liveActor && "getTreasuryFlowsForPrincipal" in liveActor) {
					const pageRes = await (
						liveActor as {
							getTreasuryFlowsForPrincipal: (
								pr: Principal,
								o: bigint,
								l: bigint,
							) => Promise<{
								events: TreasuryFlowLike[];
								total: bigint;
							}>;
						}
					).getTreasuryFlowsForPrincipal(
						p,
						BigInt(offset),
						BigInt(fetchLimit),
					);
					protocolFlows = pageRes.events ?? [];
				} else if (!liveActor) {
					protocolError =
						"Protocol actor unavailable — reconnect to load vault activity.";
				}
			} catch (e) {
				protocolError =
					e instanceof Error
						? e.message
						: "Failed to load protocol activity";
				protocolFlows = [];
			}

			const wantIndex = category === "all" || category === "transfer";
			const fromChain = await fetchUserActivity({
				agent: liveAgent,
				principal: p,
				protocolFlows,
				includeUsdmIndex: wantIndex && usdmIdx,
				includeMmkIndex: wantIndex && mmkIdx,
				indexMaxResults: fetchLimit,
			});

			const clientRows =
				category === "all" || category === "transfer"
					? loadClientActivity(principal)
					: [];

			const merged = [...fromChain];
			const seen = new Set(merged.map((e) => e.id));
			for (const c of clientRows) {
				if (!seen.has(c.id)) {
					merged.push(c);
					seen.add(c.id);
				}
			}
			merged.sort((a, b) => {
				if (a.timestampNs === b.timestampNs) return 0;
				return a.timestampNs > b.timestampNs ? -1 : 1;
			});

			const filtered =
				category !== "all" || asset !== "all" || fromDay || toDay
					? filterActivity(merged, {
							category,
							asset,
							fromDay,
							toDay,
						})
					: merged;

			const total = filtered.length;
			const start = page * pageSize;
			const entries = filtered.slice(start, start + pageSize);
			const sources = ["protocol_flow"];
			if (usdmIdx) sources.push("usdm_index");
			if (mmkIdx) sources.push("mmk_index");
			if (clientRows.length) sources.push("client");

			return {
				entries,
				total,
				page,
				pageSize,
				hasNext: start + pageSize < total,
				sources,
				protocolError,
			};
		},
		enabled: isAuthenticated && !!principal && !isInitializing,
		staleTime: 15_000,
		refetchInterval: 30_000,
	});

	return {
		...query,
		entries: query.data?.entries ?? [],
		total: query.data?.total ?? 0,
		hasNext: query.data?.hasNext ?? false,
		pageSize: query.data?.pageSize ?? pageSize,
		sources: query.data?.sources ?? [],
		protocolError: query.data?.protocolError ?? null,
		usdmIdx,
		mmkIdx,
	};
}
